import {useState, useContext, useEffect, useMemo} from 'react';

import {ErrorMessage} from 'formik';
import {useTranslation} from 'react-i18next';

import {Autocomplete, Box, Grid, TextField, Typography, useTheme} from '@mui/material';

import {EmpionSkillChip} from '../EmpionSkillChip/EmpionSkillChip';

import {StandardSkill} from '../../../src/features/company/profile/types/index';
import {LanguageContext} from '../../contexts/LanguageContext/LanguageContext';
import {StandardSkillsService} from '../../features/company/profile/services/StandardSkillsService';
import {getFieldStyle} from '../../layouts/theme/FieldStyles';

// For forms controlled by Formik, use the formik prop and leave customForm undefined
// For forms not controlled by Formik, use the customForm prop and leave formik undefined
interface CustomForm {
  values: any;
  setFieldValue: (field: string, value: any) => void;
}

interface EmpionSkillsInputFieldProps {
  formik?: any;
  customForm?: CustomForm;
  autocompleteOptions?: string[];
}

export const EmpionSkillsInputField = ({formik, customForm}: EmpionSkillsInputFieldProps) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const currentLanguage = useContext(LanguageContext);
  const [standardSkills, setStandardSkills] = useState<StandardSkill[]>([]);

  const standardSkillsService = useMemo(() => new StandardSkillsService(), []);

  const fetchStandardSkills = async () => {
    try {
      const response = await standardSkillsService.getStandardSkills(currentLanguage);
      setStandardSkills(response);
      return response;
    } catch (error) {
      console.error('Failed to fetch standard skills:', error);
    }
  };

  useEffect(() => {
    fetchStandardSkills();
    //eslint-disable-next-line
  }, []);

  const [skillsList, setSkillsList] = useState<StandardSkill[]>([]);

  useEffect(() => {
    if (formik?.values.skills) {
      setSkillsList(formik.values.skills);
    }
  }, [formik?.values.skills]);

  // String || null has to be added to the type because of the Autocomplete mui.
  const [standardSkill, setStandardSkill] = useState<StandardSkill | string | null>({
    resourceId: '',
    name: {en: '', de: ''},
  });

  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleAddNewSkill = (value: StandardSkill) => {
    if (!value) {
      return;
    }

    if (skillsList.map((skill) => skill.resourceId).includes(value?.resourceId)) {
      const translationKey = 'companyJobPostings.newJobDialog.formValidations.skillAlreadyAdded';
      setErrorMessage(t(translationKey).toString());
      return;
    }

    if (!standardSkills.find((skill) => skill.resourceId === value?.resourceId)) {
      const translationKey = 'companyJobPostings.newJobDialog.formValidations.skillNotIncluded';
      setErrorMessage(t(translationKey).toString());
      return;
    }

    const skill: StandardSkill = {
      name: value.name,
      resourceId: value.resourceId,
    };

    const updatedSkills = [...skillsList, skill];
    setSkillsList(updatedSkills);

    if (formik) {
      formik.setFieldValue('skills', updatedSkills);
    }

    if (customForm) {
      customForm.setFieldValue('skills', updatedSkills);
    }

    setErrorMessage('');
    setStandardSkill({resourceId: '', name: {en: '', de: ''}});
  };
  return (
    <Grid container spacing={1} alignItems={'start'} flexDirection='column'>
      <Grid item xs={12} sm={6} minWidth='50%' alignItems={'center'} display={'flex'} gap={1}>
        <Grid item xs={12}>
          <Autocomplete
            options={standardSkills}
            fullWidth
            freeSolo={true}
            value={standardSkill}
            getOptionLabel={(skill: any) => {
              if (!skill?.name) {
                return skill;
              }
              return skill.name[currentLanguage];
            }}
            onChange={(event, value: StandardSkill) => {
              event.preventDefault();
              event.stopPropagation();
              handleAddNewSkill(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label={t('companyJobPostings.newJobDialog.formPlaceholders.skillName')}
                name='standardCategory'
                sx={getFieldStyle(theme)}
              />
            )}
          />
        </Grid>
      </Grid>
      {errorMessage && (
        <Typography variant='body2' color='error' ml={1}>
          {errorMessage}
        </Typography>
      )}
      <Box>
        <Typography variant='body2' color='error' ml={1}>
          {formik && <ErrorMessage name='skills' component='span' />}
        </Typography>
      </Box>
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem'}}>
        {skillsList.map((skill, index) => (
          <EmpionSkillChip
            key={index}
            name={skill.name[currentLanguage]}
            onDelete={() => {
              const updatedSkillsList = skillsList.filter((item) => item.name !== skill.name);
              setSkillsList(updatedSkillsList);
              if (formik) {
                formik.setFieldValue('skills', updatedSkillsList);
              }

              if (customForm) {
                customForm.setFieldValue('skills', updatedSkillsList);
              }
            }}
          />
        ))}
      </Box>
    </Grid>
  );
};
